/**
 * Common icon components used across Mule Refactor slides
 */

interface MenuFileIconProps {
  /** Icon size in pixels */
  size?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Menu file icon component (MD filetype)
 * Used in file menu items within dropdowns
 */
export function MenuFileIcon({ size = 16, className = '' }: MenuFileIconProps) {
  return (
    <svg
      width={size * 0.875}
      height={size}
      viewBox='0 0 14 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M14 4.5V14C14 14.5304 13.7893 15.0391 13.4142 15.4142C13.0391 15.7893 12.5304 16 12 16H9V15H12C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V4.5H11C10.6022 4.5 10.2206 4.34196 9.93934 4.06066C9.65804 3.77936 9.5 3.39782 9.5 3V1H4C3.73478 1 3.48043 1.10536 3.29289 1.29289C3.10536 1.48043 3 1.73478 3 2V11H2V2C2 1.46957 2.21071 0.960859 2.58579 0.585786C2.96086 0.210714 3.46957 0 4 0L9.5 0L14 4.5ZM0.706 13.189V15.849H0V11.85H0.806L1.946 14.446H1.972L3.112 11.85H3.912V15.849H3.196V13.189H3.158L2.212 15.348H1.696L0.744 13.188H0.706V13.189ZM4.625 15.849V11.85H6.084C6.49 11.85 6.825 11.928 7.089 12.084C7.352 12.241 7.549 12.467 7.678 12.764C7.808 13.061 7.874 13.419 7.874 13.839C7.874 14.261 7.808 14.623 7.678 14.923C7.547 15.224 7.348 15.453 7.083 15.612C6.819 15.77 6.486 15.849 6.083 15.849H4.626H4.625ZM5.978 12.495H5.416V15.202H5.978C6.164 15.202 6.325 15.174 6.462 15.12C6.59372 15.0671 6.70899 14.9801 6.796 14.868C6.88965 14.7423 6.95637 14.5986 6.992 14.446C7.037 14.278 7.059 14.081 7.059 13.854C7.06625 13.598 7.0266 13.3428 6.942 13.101C6.87756 12.9146 6.75304 12.7549 6.588 12.647C6.429 12.545 6.226 12.495 5.978 12.495V12.495Z'
        fill='rgba(0,0,0,0.88)'
      />
    </svg>
  )
}

interface CheckCircleIconProps {
  /** Whether the checkbox is checked/selected */
  checked?: boolean
  /** Icon size in pixels */
  size?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Circular checkbox icon component
 * Shows a purple filled circle with checkmark when checked,
 * or a grayed checkbox (same shape but gray) when unchecked/loading
 */
export function CheckCircleIcon({
  checked = true,
  size = 16,
  className = ''
}: CheckCircleIconProps) {
  if (checked) {
    return (
      <svg
        width={size}
        height={size}
        viewBox='0 0 16 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={className}
      >
        <path
          d='M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1Z'
          fill='#65558F'
        />
        <path
          d='M11.5 5.5L7 10.5L4.5 8'
          stroke='white'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    )
  }

  // Grayed checkbox - same shape as checked but with disabled/loading color
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M8 1.6C4.46538 1.6 1.6 4.46538 1.6 8C1.6 11.5346 4.46538 14.4 8 14.4C11.5346 14.4 14.4 11.5346 14.4 8C14.4 4.46538 11.5346 1.6 8 1.6ZM0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8ZM12.1657 5.76569L11.0343 4.63431L6.4 9.26863L4.63431 7.50294L3.50294 8.63431L6.4 11.5314L12.1657 5.76569Z'
        fill='rgba(38, 38, 38, 0.2)'
      />
    </svg>
  )
}

interface DocumentIconProps {
  /** Icon size in pixels */
  size?: number
  /** Icon color (CSS color string) */
  color?: string
  /** Whether the icon is in a selected state */
  selected?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Document/File icon component
 * Used in file lists, viewers, and dropdowns
 */
export function DocumentIcon({
  size = 16,
  color = 'currentColor',
  selected = false,
  className = ''
}: DocumentIconProps) {
  const strokeColor = selected ? '#5B5BD5' : color

  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M2.5 2h8l3.5 3.5V14a1 1 0 01-1 1H2.5a1 1 0 01-1-1V3a1 1 0 011-1z'
        stroke={strokeColor}
        strokeWidth='1.5'
        fill='none'
      />
      <path d='M10.5 2v3.5H14' stroke={strokeColor} strokeWidth='1.5' fill='none' />
    </svg>
  )
}

interface ChevronIconProps {
  /** Whether the chevron is in expanded state (rotated 180deg) */
  isExpanded: boolean
  /** Icon size in pixels */
  size?: number
  /** Icon color (CSS color string) */
  color?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Chevron down/up icon component
 * Automatically rotates based on isExpanded prop
 */
export function ChevronIcon({
  isExpanded,
  size = 16,
  color = '#8e8e8e',
  className = ''
}: ChevronIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${className}`}
    >
      <path
        d='M4 6L8 10L12 6'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

interface CodeIconProps {
  /** Icon size in pixels */
  size?: number
  /** Icon color (CSS color string) */
  color?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Code brackets icon component
 * Used to represent code files or sections
 */
export function CodeIcon({ size = 16, color = '#65558f', className = '' }: CodeIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M5.33333 11.3333L2 8L5.33333 4.66667M10.6667 4.66667L14 8L10.6667 11.3333M9.33333 2.66667L6.66667 13.3333'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

interface DownloadIconProps {
  /** Icon size in pixels */
  size?: number
  /** Icon color (CSS color string) */
  color?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Download icon component
 * Used in download buttons
 */
export function DownloadIcon({
  size = 16,
  color = 'rgba(0,0,0,0.88)',
  className = ''
}: DownloadIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 6.66667L8 10M8 10L4.66667 6.66667M8 10V2'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

interface ExpandIconProps {
  /** Icon size in pixels */
  size?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Expand/fullscreen icon component
 * Used to open items in full screen
 */
export function ExpandIcon({ size = 16, className = '' }: ExpandIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M13.4194 2H9.28516C8.96448 2 8.70452 2.26233 8.70452 2.58594C8.70452 2.90954 8.96448 3.17188 9.28516 3.17188H11.9097L3.16129 12V9.24219C3.16129 8.91858 2.90133 8.65625 2.58065 8.65625C2.25996 8.65625 2 8.91858 2 9.24219V13.4141C2.00417 13.7359 2.26171 13.9958 2.58065 14H6.71484C7.03552 14 7.29548 13.7377 7.29548 13.4141C7.29548 13.0905 7.03552 12.8281 6.71484 12.8281H3.98194L12.8387 3.89063V6.75781C12.8387 7.08142 13.0987 7.34375 13.4194 7.34375C13.74 7.34375 14 7.08142 14 6.75781V2.58594C13.9958 2.26409 13.7383 2.00421 13.4194 2Z'
        fill='black'
      />
    </svg>
  )
}

interface EditIconProps {
  /** Icon size in pixels */
  size?: number
  /** Icon color (CSS color string) */
  color?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Edit/pen icon component
 * Used to indicate editable content
 */
export function EditIcon({ size = 16, color = 'rgba(0,0,0,0.88)', className = '' }: EditIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M9.33333 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V6.66667M5.33333 10.6667H6.66667L12 5.33333C12.2652 5.06812 12.4142 4.70644 12.4142 4.33V4.33C12.4142 3.95356 12.2652 3.59188 12 3.32667C11.7348 3.06146 11.3731 2.91242 10.9967 2.91242C10.6202 2.91242 10.2585 3.06146 9.99333 3.32667L4.66667 8.66V10V10.6667V10.6667Z'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}
